import * as React from 'react';
import { BrandLogos } from '../../../services/modules/brand/brand.types';
import { IntegrationConfigName } from '../../integrations/integrations.types';
import { UsedInOfflineMode } from '../../offline/types/UsedInOfflineMode';
import { OrderStatus } from '../../orders/interfaces/order-status.interface';
import { PaymentGatewayNameEnum } from '../../payment-gateway/enums/payment-gateway-name.enum';
import { LicenseType, UserPosition, UserRole } from '../../users/roles';

export enum Actions {
  LOADING_BRANDS = 'context/loading_brands',
  SET_USER = 'context/user',
  SET_ADMIN_TECEO = 'context/admin_teceo',
  LOAD_CONTEXT = 'context/load',
  SET_BRAND = 'context/set_change',
  CONFIGURE_CONTEXT = 'context/configure',
  HIDE_CONTACT_BRAND_FLOAT_BUTTON = 'context/hide_contact_brand_float_button',
  RESET_CONTACT_BRAND_FLOAT_BUTTON_STATE = 'context/reset_contact_brand_float_button_state',
}

export interface BrandIntegration {
  active: boolean;
  id: string;
  name: IntegrationConfigName;
  integrationTrigger: OrderStatus;
  hasCustomerIntegration: boolean;
  canForceReverseOrderSync: boolean;
  hasOrderIntegration: boolean;
  hasAutoImportAccountsReceivable?: boolean;
}

interface BrandPaymentGateways {
  id: string;
  name: PaymentGatewayNameEnum;
  refundLimitHours: number;
}

export interface Brand {
  id: string;
  name: string;
  logoUrl?: string;
  logos?: BrandLogos;
  slug: string;
  verified: boolean;
  logo?: string;
  integrations?: Array<BrandIntegration>;
  customerSupportPhone?: string;
  paymentGateways?: BrandPaymentGateways[];
  connectedAt?: string;
}

export type Store = {
  id: string;
  name: string;
  imageUrl?: string;
  logoUrl?: string;
  slug?: string;
  verified: boolean;
  inPendingVerification: boolean;
};

export type Team = {
  email: string;
  id: string;
  lastAccess: Date;
  licenseType: LicenseType;
  name: string;
  phone: string;
  countryCode: string;
  position: UserPosition;
  role: UserRole;
  roleName: string;
  roleId: string;
  isCustomRole: boolean;
  surname: string;
  userId: string;
  profileUrl?: string;
  store?: Store;
  brand?: Brand;
  shouldShowNps: boolean;
  shouldShowPos: boolean;
};

export type ActionType = {
  type: Actions;
  payload?: Partial<ApplicationContextState>;
};

export type ContextOptions = {
  showBrand?: boolean;
  disableBrand?: boolean;
};

export interface ApplicationContextState {
  brands: Brand[];
  brand?: Brand;
  isLoadingBrands: boolean;
  options: ContextOptions;
  user?: Team;
  isAdminTeceo?: boolean;
  hideContactBrandButton?: boolean;
}

export type ApplicationContextDispatch = React.Dispatch<ActionType>;

export interface ApplicationContextProps {
  state: ApplicationContextState;
  isCustomRole: boolean;
  brandSlug?: string;
  team?: Team;
  userRole: UserRole;
  dispatch: ApplicationContextDispatch;
  changeTeam: (team: Team) => void;
  changeTeamInOffline: (team: Team) => void;
  changeBrand: (brand: Brand) => void;
  removeSelectedBrand: () => void;
  loadTeam: () => Promise<void>;
  loadBrands: () => Promise<void>;
  isAdminBrand: boolean;
  isAdminStore: boolean;
  isSeller: boolean;
  isBuyer: boolean;
  isBrand: boolean;
  isStore: boolean;
  brandId?: string;
  hasHopeIntegration: boolean;
  hasBrandIntegratorIntegration: boolean;
  hasVerupIntegration: boolean;
  hasIntegrations: boolean;
  hasLinxIntegration: boolean;
  hasEmillenniumIntegration: boolean;
  hasCustomerIntegration: boolean;
  canForceReverseOrderSync: boolean;
  integrations?: BrandIntegration[];
  hasTotvsIntegration: boolean;
  showContactBrandButton: boolean;
  hasOrderIntegration: boolean;
  isEnabledHideOrderValue: boolean;
  hasAutoImportAccountsReceivable: boolean;
  previousLocation?: string;
  hasPaymentGateway: boolean;
  erpIntegration?: BrandIntegration;
  brandPaymentGateways: BrandPaymentGateways[];
}

export interface ApplicationContextProviderProps extends UsedInOfflineMode {
  children: React.ReactNode;
}
